import { useEffect } from 'react';

export interface KeyboardShortcutHandlers {
  onGenerateExcel?: () => void;
  onGeneratePdf?: () => void;
  onPreview?: () => void;
  onFocusSearch?: () => void;
}

const isTypingInField = (el: EventTarget | null): boolean => {
  if (!(el instanceof HTMLElement)) return false;
  const tag = el.tagName.toLowerCase();
  return tag === 'input' || tag === 'textarea' || tag === 'select' || el.isContentEditable;
};

/**
 * Registers global keyboard shortcuts on `window`:
 * `/` focuses search, `Ctrl/Cmd+Enter` generates Excel,
 * `Ctrl/Cmd+Shift+Enter` generates PDF, and `Ctrl/Cmd+P` opens preview.
 * Plain-key shortcuts are suppressed while typing in form fields;
 * modifier combos always fire.
 */
export function useKeyboardShortcuts(handlers: KeyboardShortcutHandlers): void {
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      const isMod = event.metaKey || event.ctrlKey;
      const typing = isTypingInField(event.target);

      if (isMod && event.key === 'Enter') {
        event.preventDefault();
        if (event.shiftKey) {
          handlers.onGeneratePdf?.();
        } else {
          handlers.onGenerateExcel?.();
        }
        return;
      }

      if (isMod && (event.key === 'p' || event.key === 'P')) {
        event.preventDefault();
        handlers.onPreview?.();
        return;
      }

      if (event.key === '/' && !isMod && !typing) {
        event.preventDefault();
        handlers.onFocusSearch?.();
        return;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [handlers]);
}
